import { startTransition, useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { prefersReducedMotion } from '@/components/layout/shell/entrance';
import type { Courier } from './courier/courier';
import {
  EVENT_PLAN,
  HOME_GESTURE_MS,
  COALESCE_MS,
  DEFER_TICK_MS,
  FLIGHT_GAP_MS,
  QUEUE_MAX_MS,
  REACT_GAP_MS,
  RING_MS,
  SHOW_GRACE_MS,
  SUCCESS_HOLD_MS,
  anchorIds,
  isAnchorVisible,
  isTypingTarget,
  liveText,
  pickAnchor,
  topOf,
} from './observerEvents';
import type {
  AnchorMeta,
  CorePort,
  CoreOrigin,
  HeldState,
  ObserverEvent,
  ObserverEventKind,
  ObserverSettings,
  ObserverState,
} from './observerContext';

/*
  Движок помощника в оболочке (дом core, spec-3 §2–§5, §9). Своего тела нет: дом — ядро знака или
  выделение пункта (CorePort от главного меню).

  Приём события — за кадр, до всякого движения: долгое состояние встаёт в стопку, «готово» вспыхивает,
  строка уходит читалке. Признаки события (счётчик, точка, тост) ставит тот, кто владеет данными, — ядро
  ничего не сообщает само, оно только показывает, где это.

  Реакция — по очереди, одна за раз, по приоритету; тот же ключ за 1,5 с — один показ; прождавшее
  дольше 10 с дома реакции не получает. Дом ещё не в покое (знакомство, загрузка, перелёт ядра в меню) —
  событие ждёт; дом спрятан («Фокус») — реакция без дома: только кольцо на цели. Режим «Показывает,
  где» ведёт к событию курьером (CoreCourier, spec-3-motion): не чаще раза в 6 с и не раньше 600 мс после
  показа страницы; человек занят (печатает, тащит вкладку), раскладка едет или окно цели прокручивают — путь
  ждёт до 10 с, потом жест дома. Курьер отказал (нет пути, тесно, далеко, курсор у цели) — «На месте»: жест
  дома и кольцо на цели. Пока идёт визит, очередь стоит; событие старше сокращает удержание, событие на ту же
  цель играет жестом на месте.
*/

export interface Anchor {
  el: HTMLElement;
  meta: AnchorMeta;
}

interface QueueItem {
  kind: ObserverEventKind;
  ids: string[];
  key: string;
  /** Когда пришло, performance.now(). */
  at: number;
  priority: number;
  /** Сколько схлопнулось в этот показ. */
  n: number;
  /** Ждёт, пока человек занят (печатает, тащит вкладку): до 10 с, потом — жест дома. */
  waitedBusy?: boolean;
}

interface CoreEngineOptions {
  /** Движок включён только в доме core; на площадке (orbit) хуки стоят без дела. */
  active: boolean;
  settings: ObserverSettings;
  settingsRef: RefObject<ObserverSettings>;
  anchorsRef: RefObject<Map<string, Anchor[]>>;
  suspended: boolean;
  /** Курьер ядра (CoreCourier кладёт его сюда). */
  courierRef: RefObject<Courier | null>;
}

export interface CoreEngine {
  state: ObserverState;
  holds: Readonly<Record<string, HeldState>>;
  awaiting: boolean;
  away: boolean;
  notify: (event: ObserverEvent) => void;
  setState: (s: ObserverState) => void;
  hold: (key: string, s: HeldState) => void;
  resolve: (key: string) => void;
  setCorePort: (port: CorePort | null) => void;
  /** Отклик дома вне очереди (react на площадке): жест на ядре или выделении. */
  cueHome: (gesture: Parameters<CorePort['cue']>[0]['gesture']) => void;
  politeRef: RefObject<HTMLDivElement | null>;
  assertiveRef: RefObject<HTMLDivElement | null>;
}

/** Ключ системного состояния — «Состояние системы» на верхней полосе «Основы». */
const SYSTEM_KEY = 'system';
/** Пауза перед строкой для читалки: снятая и поставленная заново строка читается повторно. */
const LIVE_RESET_MS = 60;

export function useCoreEngine({
  active,
  settings,
  settingsRef,
  anchorsRef,
  suspended,
  courierRef,
}: CoreEngineOptions): CoreEngine {
  const [holds, setHolds] = useState<Record<string, HeldState>>({});
  const holdsRef = useRef(holds);
  const [flash, setFlash] = useState(false);
  // Ядро у события: курьер отдал его капле и ещё не вернул (меняется дважды за визит).
  const [away, setAway] = useState(false);

  const politeRef = useRef<HTMLDivElement>(null);
  const assertiveRef = useRef<HTMLDivElement>(null);
  const portRef = useRef<CorePort | null>(null);
  const queue = useRef<QueueItem[]>([]);
  const lastKey = useRef(new Map<string, { at: number; n: number }>());
  const busyUntil = useRef(0);
  const pumpTimer = useRef(0);
  const flashTimer = useRef(0);
  /** Своя пауза у каждой области читалки: срочная строка не глотает вежливую, и наоборот. */
  const liveTimers = useRef({ polite: 0, assertive: 0 });
  /** Строки, ждущие паузы, по ключу события: схлопнутое заменяет свою строку, разные — идут вместе. */
  const livePending = useRef({ polite: new Map<string, string>(), assertive: new Map<string, string>() });
  const ring = useRef<{ el: HTMLElement; timer: number } | null>(null);
  const pumpRef = useRef<() => void>(() => undefined);
  /** Идёт перетаскивание (вкладки): человек занят. */
  const dragging = useRef(false);
  /** Открыт ящик, поиск или ИИ: курьер не летит, визит прерывается. */
  const suspendedRef = useRef(suspended);
  /** Последний полёт, показ страницы (переход по меню), курсор, идущий визит. */
  const lastFlight = useRef(-Infinity);
  const shownAt = useRef(0);
  const cursor = useRef<{ x: number; y: number } | null>(null);
  const visiting = useRef<QueueItem | null>(null);
  const portOff = useRef<(() => void) | null>(null);
  useEffect(() => {
    suspendedRef.current = suspended;
    if (suspended) courierRef.current?.suspend();
  }, [suspended, courierRef]);

  // ---------- стопка долгих состояний ----------

  const hold = useCallback((key: string, s: HeldState) => {
    const prev = holdsRef.current;
    if (prev[key] === s) return;
    const next = { ...prev, [key]: s };
    holdsRef.current = next;
    setHolds(next);
  }, []);

  const resolve = useCallback((key: string) => {
    const prev = holdsRef.current;
    if (!(key in prev)) return;
    const next = { ...prev };
    delete next[key];
    holdsRef.current = next;
    setHolds(next);
  }, []);

  /** «Готово» — вспышка поверх стопки: держится от события, потом дом уходит к верхнему долгому состоянию. */
  const flashSuccess = useCallback(() => {
    setFlash(true);
    window.clearTimeout(flashTimer.current);
    // Конец вспышки — не срочно: перерисовка оболочки не должна рвать кадры капли в пути.
    flashTimer.current = window.setTimeout(() => startTransition(() => setFlash(false)), SUCCESS_HOLD_MS);
  }, []);

  // ---------- строка для читалки ----------

  const announce = useCallback((text: string, live: 'polite' | 'assertive', key: string) => {
    const node = live === 'assertive' ? assertiveRef.current : politeRef.current;
    if (!node) return;
    // Две строки одной срочности за 60 мс — одной строкой: вторая не стирает первую (тот же ключ — замена:
    // «Новое уведомление» → «2 уведомления»).
    const pending = livePending.current[live];
    pending.set(key, text);
    node.textContent = '';
    window.clearTimeout(liveTimers.current[live]);
    liveTimers.current[live] = window.setTimeout(() => {
      node.textContent = [...pending.values()].join('. ');
      pending.clear();
    }, LIVE_RESET_MS);
  }, []);

  // ---------- кольцо на цели ----------

  const ringOff = useCallback(() => {
    const r = ring.current;
    if (!r) return;
    window.clearTimeout(r.timer);
    delete r.el.dataset.observerRing;
    ring.current = null;
  }, []);

  /** Кольцо на видимой цели (режим «На месте»): только с разрешения подсвечивать чужое, не на пунктах меню. */
  const ringOn = useCallback(
    (item: QueueItem) => {
      const all = (id: string) => (anchorsRef.current.get(id) ?? []).map((a) => a.el);
      const level = (el: HTMLElement, id: string) =>
        anchorsRef.current.get(id)?.find((a) => a.el === el)?.meta.level;
      const target = pickAnchor(item.ids, all, (el, id) => level(el, id) !== 0 && isAnchorVisible(el));
      if (!target) return;
      ringOff();
      target.el.dataset.observerRing = EVENT_PLAN[item.kind].ring;
      const el = target.el;
      ring.current = {
        el,
        timer: window.setTimeout(() => {
          delete el.dataset.observerRing;
          if (ring.current?.el === el) ring.current = null;
        }, RING_MS),
      };
    },
    [anchorsRef, ringOff],
  );

  // ---------- очередь ----------

  const schedule = useCallback((ms: number) => {
    window.clearTimeout(pumpTimer.current);
    pumpTimer.current = window.setTimeout(() => pumpRef.current(), Math.max(0, ms));
  }, []);

  /** Реакция дома на событие: жест на ядре в знаке или на выделении, кольцо на цели. */
  const play = useCallback(
    (item: QueueItem, home: CoreOrigin | null) => {
      const plan = EVENT_PLAN[item.kind];
      const now = performance.now();
      let ms = 0;
      // Этап D2: «Показывает, где» и все проверки пути — курьер к цели. Здесь — жест дома.
      if (home && !prefersReducedMotion()) {
        const g = home.kind === 'mark' ? plan.mark : plan.pill;
        if (g) {
          portRef.current?.cue({ gesture: g, at: now });
          ms = HOME_GESTURE_MS[g];
        }
      }
      if (settingsRef.current.targetRing) ringOn(item);
      busyUntil.current = now + ms + REACT_GAP_MS;
    },
    [ringOn, settingsRef],
  );

  /** Путь к событию: курьер проверяет всё до вылета; ушёл — очередь стоит до приёма ядра. */
  const fly = useCallback(
    (item: QueueItem, port: CorePort): 'flew' | 'wait' | 'refused' => {
      const courier = courierRef.current;
      if (!courier) return 'refused';
      const plan = courier.plan({
        kind: item.kind,
        ids: item.ids,
        port,
        anchors: (id) => anchorsRef.current.get(id) ?? [],
        settings: settingsRef.current,
        cursor: cursor.current,
      });
      if (!plan.go) return plan.wait ? 'wait' : 'refused';
      lastFlight.current = performance.now();
      visiting.current = item;
      void plan.start().then(() => {
        visiting.current = null;
        busyUntil.current = performance.now() + REACT_GAP_MS;
        pumpRef.current();
      });
      return 'flew';
    },
    [anchorsRef, courierRef, settingsRef],
  );

  const pump = useCallback(() => {
    const now = performance.now();
    // Идёт визит — его конец сам позовёт очередь.
    if (visiting.current) return;
    if (now < busyUntil.current) return schedule(busyUntil.current - now);
    const q = queue.current;
    for (let i = q.length - 1; i >= 0; i--)
      if (now - q[i].at > QUEUE_MAX_MS && !q[i].waitedBusy) q.splice(i, 1);
    if (q.length === 0) return;
    if (!settingsRef.current.enabled) {
      q.length = 0;
      return;
    }
    const port = portRef.current;
    const home = port ? port.origin() : null;
    // Меню ещё не встало или ядро не в покое (знакомство, загрузка, перелёт в меню) — ждём. Дом спрятан
    // («Фокус», слой поверх меню) — не ждём: реакция без дома.
    if (port && (!home || (home.shown && !home.rest))) return schedule(DEFER_TICK_MS);
    let best = 0;
    for (let i = 1; i < q.length; i++) {
      if (q[i].priority > q[best].priority || (q[i].priority === q[best].priority && q[i].at < q[best].at)) {
        best = i;
      }
    }
    // Путь к событию (этап D2) — только в режиме «Показывает, где», без ящиков и поиска поверх, без
    // уменьшенного движения, при видном доме и видной вкладке браузера (в фоне анимации стоят — визит
    // доиграл бы, когда человек вернётся). Человек занят — путь ждёт, но не дольше 10 с: прождавшее
    // дольше полёта не получает, только жест дома.
    const s = settingsRef.current;
    const wantsPath =
      s.guidance &&
      EVENT_PLAN[q[best].kind].flies &&
      !suspendedRef.current &&
      !prefersReducedMotion() &&
      !document.hidden &&
      !!home?.shown;
    const fresh = now - q[best].at < QUEUE_MAX_MS;
    const busy = dragging.current || isTypingTarget(document.activeElement);
    if (wantsPath && busy && fresh) {
      q[best].waitedBusy = true;
      return schedule(DEFER_TICK_MS);
    }
    // Не чаще раза в 6 с и не раньше 600 мс после показа страницы; второе событие раньше — жест дома.
    if (wantsPath && !busy && fresh && port && home && now - lastFlight.current >= FLIGHT_GAP_MS) {
      if (now - shownAt.current < SHOW_GRACE_MS) return schedule(SHOW_GRACE_MS - (now - shownAt.current));
      const r = fly(q[best], port);
      if (r === 'flew') {
        q.splice(best, 1);
        return;
      }
      // Раскладка едет или окно цели прокручивают — ждём, как при «человек занят», не дольше 10 с.
      if (r === 'wait') {
        q[best].waitedBusy = true;
        return schedule(DEFER_TICK_MS);
      }
    }
    const [item] = q.splice(best, 1);
    play(item, home && home.shown ? home : null);
    if (q.length > 0) schedule(busyUntil.current - performance.now());
  }, [fly, play, schedule, settingsRef]);

  useEffect(() => {
    pumpRef.current = pump;
  }, [pump]);

  const notify = useCallback(
    (ev: ObserverEvent) => {
      const plan = EVENT_PLAN[ev.kind];
      const ids = anchorIds(ev.anchorId);
      const key = ev.key ?? `${ev.kind}:${ids.join('|')}`;
      const now = performance.now();
      for (const [k, v] of lastKey.current) if (now - v.at > COALESCE_MS) lastKey.current.delete(k);
      const last = lastKey.current.get(key);
      const n = last ? last.n + 1 : 1;
      lastKey.current.set(key, { at: now, n });

      // Приём — сразу: состояние в стопку, вспышка «готово» (и конец операции своего ключа), строка читалке.
      if (plan.holds) hold(key, plan.holds);
      if (plan.flash) {
        resolve(key);
        flashSuccess();
      }
      if (ev.announce !== false) announce(liveText(ev.kind, ev.label, n), plan.live, key);

      const s = settingsRef.current;
      if (!s.enabled || !s.reactions[ev.kind]) return;
      // Схлопнулось: ждёт в очереди — растёт число; уже показано — второго показа нет.
      if (last) {
        const waiting = queue.current.find((i) => i.key === key);
        if (waiting) waiting.n = n;
        return;
      }
      // Капля у цели: событие на ту же цель — жест на месте, второго полёта нет; событие старше — удержание
      // сокращается (не короче 400 мс после посадки). От цели к цели капля не перелетает — только через дом.
      const v = visiting.current;
      const courier = courierRef.current;
      if (v && courier) {
        const els = ids.flatMap((id) => (anchorsRef.current.get(id) ?? []).map((a) => a.el));
        if (courier.replay(els, ev.kind)) return;
        if (plan.priority > EVENT_PLAN[v.kind].priority) courier.hurry();
      }
      queue.current.push({ kind: ev.kind, ids, key, at: now, priority: plan.priority, n });
      // Реакция — следующей задачей: событие часто приходит посреди нажатия (меню закрывается, фокус
      // возвращается), а видимость цели проверяется по экрану как он есть.
      schedule(0);
    },
    [anchorsRef, announce, courierRef, flashSuccess, hold, resolve, schedule, settingsRef],
  );

  /** «Состояние системы»: покой снимает, операция держит, сбой — событие (красный скачком и дрожь ядра). */
  const setState = useCallback(
    (s: ObserverState) => {
      if (s === 'idle') return resolve(SYSTEM_KEY);
      if (s === 'success') return flashSuccess();
      if (s === 'error' && holdsRef.current[SYSTEM_KEY] !== 'error') {
        return notify({ kind: 'error', key: SYSTEM_KEY, label: 'Сбой системы' });
      }
      hold(SYSTEM_KEY, s);
    },
    [flashSuccess, hold, notify, resolve],
  );

  const setCorePort = useCallback(
    (port: CorePort | null) => {
      portOff.current?.();
      portOff.current = null;
      portRef.current = port;
      if (!port) return;
      // Показ страницы: переход по меню, раскрытие, «Фокус» — пауза 600 мс до первого полёта; дорога на стенде
      // перерисовывается, когда раскладка встанет.
      shownAt.current = performance.now();
      portOff.current = port.onChange(() => {
        shownAt.current = performance.now();
        if (settingsRef.current.showRoute) {
          window.setTimeout(() => courierRef.current?.drawStreets(port), SHOW_GRACE_MS);
        }
      });
      pumpRef.current();
    },
    [courierRef, settingsRef],
  );

  const cueHome = useCallback((gesture: Parameters<CorePort['cue']>[0]['gesture']) => {
    if (prefersReducedMotion()) return;
    portRef.current?.cue({ gesture, at: performance.now() });
  }, []);

  // Курьер сообщает отлучку ядра: ритм ожидания — только дома.
  useEffect(() => {
    const courier = courierRef.current;
    if (!active || !courier) return;
    // Не срочно: смена away перерисовывает всех слушателей помощника, а кадр передачи ядра ждать не должен.
    courier.onAway = (v) => startTransition(() => setAway(v));
    return () => {
      courier.onAway = () => undefined;
    };
  }, [active, courierRef]);

  // Курсор: у цели или в ней — полёта нет; вошёл в цель на удержании — кольцо снимается.
  useEffect(() => {
    if (!active) return;
    const move = (e: PointerEvent) => {
      cursor.current = { x: e.clientX, y: e.clientY };
      courierRef.current?.pointer(e.clientX, e.clientY);
    };
    const leave = () => {
      cursor.current = null;
    };
    window.addEventListener('pointermove', move, { passive: true });
    document.documentElement.addEventListener('pointerleave', leave);
    return () => {
      window.removeEventListener('pointermove', move);
      document.documentElement.removeEventListener('pointerleave', leave);
    };
  }, [active, courierRef]);

  // Стенд: «Показать дорогу» — улицы дома пунктиром.
  useEffect(() => {
    if (!active) return;
    courierRef.current?.setShowRoute(settings.showRoute, portRef.current);
  }, [active, settings.showRoute, courierRef]);

  // Перетаскивание вкладки — человек занят.
  useEffect(() => {
    if (!active) return;
    const on = () => {
      dragging.current = true;
    };
    const off = () => {
      dragging.current = false;
    };
    window.addEventListener('dragstart', on, true);
    window.addEventListener('dragend', off, true);
    window.addEventListener('drop', off, true);
    return () => {
      window.removeEventListener('dragstart', on, true);
      window.removeEventListener('dragend', off, true);
      window.removeEventListener('drop', off, true);
    };
  }, [active]);

  // Помощник выключен — очередь и кольцо снимаются сразу; признаки и строка читалки остаются.
  useEffect(() => {
    if (settings.enabled) return;
    queue.current.length = 0;
    ringOff();
  }, [settings.enabled, ringOff]);

  useEffect(() => {
    if (!active) return;
    const timers = liveTimers.current;
    return () => {
      window.clearTimeout(pumpTimer.current);
      window.clearTimeout(flashTimer.current);
      window.clearTimeout(timers.polite);
      window.clearTimeout(timers.assertive);
      portOff.current?.();
      ringOff();
    };
  }, [active, ringOff]);

  const top = topOf(holds);
  const state: ObserverState = flash ? 'success' : top;
  const awaiting = active && state === 'asking' && settings.enabled && !away;

  return useMemo(
    () => ({
      state,
      holds,
      awaiting,
      away,
      notify,
      setState,
      hold,
      resolve,
      setCorePort,
      cueHome,
      politeRef,
      assertiveRef,
    }),
    [state, holds, awaiting, away, notify, setState, hold, resolve, setCorePort, cueHome],
  );
}
